import { z } from 'zod';
import { isoDate, uuid } from './common';

/**
 * A subject that keeps coming back (PRD 52).
 *
 * ⚠ Everything countable is **derived at read time** from the mentions — the
 * trend, the totals, who raised it most. Nothing that can be counted is
 * stored, because a stored count is a count that can be wrong, and this page
 * is entirely made of counts.
 */
export const topicRow = z.object({
  id: uuid,
  name: z.string(),
  description: z.string().nullable(),
  /** How many MEETINGS it came up in. Never how many times it was uttered. */
  mentions: z.number().int(),
  /**
   * ⚠ The number that earns a topic its place: how many DIFFERENT places it
   * crossed, where every instance of one recurring meeting counts once. A
   * subject confined to a single series is that series, and the series has its
   * own page — so this is the figure the list leads with, not the raw count.
   */
  places: z.number().int(),
  /** Those places, named — "Alpha Daily", "Vendor call". What makes it a topic. */
  placeNames: z.array(z.string()),
  firstRaisedAt: isoDate,
  lastMentionedAt: isoDate,
  /** Who brought it up in the most meetings, for the row's face. */
  mostActive: z.object({ name: z.string(), meetings: z.number().int() }).nullable(),
  /** Mentions per week, oldest first — the sparkline, and nothing more. */
  trend: z.array(z.number().int()),
  /** Nothing has mentioned it for a season; derived, never stored. */
  quiet: z.boolean(),
});
export type TopicRow = z.infer<typeof topicRow>;

export const topicsList = z.object({
  topics: z.array(topicRow),
  /** How many weeks the trend covers, so the axis can be labelled honestly. */
  weeks: z.number().int(),
});
export type TopicsList = z.infer<typeof topicsList>;

/** One thing a meeting did to a topic — the timeline's rows. */
export const topicEvent = z.object({
  kind: z.enum(['action', 'decision', 'risk', 'question']),
  id: uuid,
  text: z.string(),
  detail: z.string().nullable(),
  status: z.string(),
});
export type TopicEvent = z.infer<typeof topicEvent>;

/** One meeting's worth of a topic: what it did, and what came out of it. */
export const topicMoment = z.object({
  meeting: z.object({ id: uuid, title: z.string(), startedAt: isoDate }),
  summary: z.string().nullable(),
  speakers: z.array(z.string()),
  startSegmentId: uuid.nullable(),
  events: z.array(topicEvent),
});
export type TopicMoment = z.infer<typeof topicMoment>;

export const topicDetail = topicRow.extend({
  /** Where it stands — null until the topic has moved enough to be worth saying. */
  stands: z.string().nullable(),
  standsAt: isoDate.nullable(),
  /** Newest first: one block per meeting. */
  timeline: z.array(topicMoment),
  /** Everyone who has raised it, most meetings first. */
  raisedBy: z.array(z.object({ name: z.string(), meetings: z.number().int() })),
  weeks: z.number().int(),
});
export type TopicDetail = z.infer<typeof topicDetail>;

/**
 * One of this person's other threads, offered as the one a topic is the same
 * work as. Closest by meaning first.
 */
export const topicCandidate = z.object({
  id: uuid,
  name: z.string(),
  description: z.string().nullable(),
  /** Crossed into a second place, so it is on the topics page. */
  live: z.boolean(),
  /** How many meetings it came up in — the ones this person can open. */
  mentions: z.number().int(),
  placeNames: z.array(z.string()),
  lastMentionedAt: isoDate,
  /**
   * Near enough by meaning that the linker would have asked about it
   * (`LOOK_WITHIN`) — the band where a missed thread usually is.
   */
  likely: z.boolean(),
});
export type TopicCandidate = z.infer<typeof topicCandidate>;

export const topicCandidates = z.object({ topics: z.array(topicCandidate) });
export type TopicCandidates = z.infer<typeof topicCandidates>;

/** "Same work as…" — fold this thread into `into`, another of your own. */
export const mergeTopicBody = z.object({
  into: uuid,
  /** Keep THIS thread's name on the one that is left, instead of its own. */
  keepName: z.boolean(),
});
export type MergeTopicBody = z.infer<typeof mergeTopicBody>;

/** The thread that is left — where the page goes next. */
export const topicMerged = z.object({ id: uuid });
export type TopicMerged = z.infer<typeof topicMerged>;

/**
 * A person's own name for their thread (Vladimir, 2026-09-24: "Add options to
 * rename topics"). What it is matched by stays what it is about — the name is
 * the label a person reads.
 */
export const renameTopicBody = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(300).nullable().optional(),
});
export type RenameTopicBody = z.infer<typeof renameTopicBody>;
